"use client"

import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { handleFormChange } from "@/utils/formChange"
import { Loader2, Plus } from "lucide-react"
import { FC, FormEvent, useState } from "react"
import { toast } from "sonner"
import { createVehicleClass } from "../services/vehicle_service"

interface Props {
    currency: string;
    title: string;
    description?: string;
    onSaved?: () => void;
}

const Create: FC<Props> = ({ currency, title, description, onSaved }) => {

    const [formProp, setFormProp] = useState({
        name: "",
        description: "",
        base_price: "",
    });

    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false)

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsOpen(true);
    }

    const submitForm = async (e: FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            await createVehicleClass(formProp);

            toast('Category added successfully', {
                description: 'Vehicle category was created successfully!',
            });
            setIsOpen(false);
            setLoading(false);

            if (onSaved) {
                onSaved();
            }

            setFormProp({
                name: "",
                description: "",
                base_price: "",
            });
        }
        catch (error) {
            console.error(error);
            toast('An error occured. Please try again')
            setLoading(false);
        }
    }

    return (
        <div>
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogTrigger onClick={handleClick} asChild>
                    <Button>
                        <Plus className="h-4 w-4" />
                        {title}
                    </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[600px]">
                    <DialogHeader>
                        <DialogTitle>{title}</DialogTitle>
                        {description && <DialogDescription>{description}</DialogDescription>}
                    </DialogHeader>
                    <form onSubmit={submitForm} className="grid mt-4 space-y-4">
                        <div className="grid gap-1">
                            <Label htmlFor="name" className="text-right">
                                Category name *
                            </Label>
                            <Input id="name" name="name" type="text" value={formProp.name}
                                onChange={(e: FormEvent<HTMLInputElement>) => handleFormChange(e, setFormProp)}
                                placeholder="Name of category" required />
                        </div>
                        <div className="grid gap-1">
                            <Label htmlFor="description" className="text-right">
                                Description
                            </Label>
                            <Input id="description" name="description" type="text" value={formProp.description}
                                onChange={(e: FormEvent<HTMLInputElement>) => handleFormChange(e, setFormProp)} placeholder="Description" />
                        </div>
                        <div className="grid gap-1">
                            <Label htmlFor="base_price" className="text-right">
                                Base price ({currency})
                            </Label>
                            <Input id="base_price" name="base_price" type="number" min={0} value={formProp.base_price}
                                onChange={(e: FormEvent<HTMLInputElement>) => handleFormChange(e, setFormProp)} placeholder="Base price" />
                        </div>
                        <div className="grid grid-cols-3 gap-4 mt-8">
                            <Button type="submit" disabled={loading}>
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    "Save changes"
                                )}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Create;
